import { sidebarPage } from '../utils/sidebarPageLayout.js';
import { fitnessData } from '../data/fitnessData.js';
import { db, auth } from '../../src/firebase.js';
import { 
    collection, 
    query, 
    where, 
    onSnapshot, 
    addDoc, 
    doc,
    serverTimestamp 
} from "firebase/firestore";
import { signInWithGoogle, resetPassword } from '../../src/auth.js';

const SUBSECTION_MAPPING = {
    'hiit': 'hiit-high-intensity-interval-training',
    'cycling': 'indoor-outdoor-cycling',
    'running': 'running-walking',
    'kickboxing': 'kickboxing-boxing',
    'jump-rope': 'jump-rope',
    'rowing': 'rowing',
    'weightlifting': 'weightlifting-weight-training',
    'calisthenics': 'pull-up-bar-calisthenics',
    'kettlebell': 'kettlebell-training',
    'pilates': 'pilates',
    'barre': 'barre',
    'bands': 'resistance-bands',
    'trx': 'trx-suspension-training',
    'crossfit': 'crossfit-boot-camp',
    'yoga': 'yoga',
    'stretching': 'stretching',
    'meditation': 'meditation-breathwork',
    'mobility': 'mobility-recovery',
    'tai-chi': 'tai-chi-qigong',
    'swimming': 'swimming-aqua-fitness',
    'dance': 'dance-fitness',
    'chair': 'chair-fitness'
};

const FITNESS_DATA = {
    'cardio': {
        title: 'Cardio & High-Intensity',
        icon: '⚡',
        image: '/images/fitness/cardio.png',
        desc: 'Burn fat and improve endurance.',
        subsections: [
            { id: 'hiit', title: 'HIIT (High-Intensity Interval Training)', desc: 'Short bursts of maximum effort followed by rest.', icon: '⏱️' },
            { id: 'cycling', title: 'Indoor & Outdoor Cycling', desc: 'Including high-energy "Spin" style classes.', icon: '🚲' },
            { id: 'running', title: 'Running & Walking', desc: 'GPS-tracked outdoor sessions or treadmill routines.', icon: '👟' },
            { id: 'kickboxing', title: 'Kickboxing & Boxing', desc: 'Combat-inspired cardio for coordination and stress relief.', icon: '🥊' },
            { id: 'jump-rope', title: 'Jump Rope', desc: 'Excellent for agility and space-efficient cardio.', icon: '➰' },
            { id: 'rowing', title: 'Rowing', desc: 'Full-body endurance training often used in circuit programs.', icon: '🛶' }
        ]
    },
    'strength': {
        title: 'Strength & Toning',
        icon: '💪',
        image: '/images/fitness/strength.png',
        desc: 'Build muscle and sculpt your body.',
        subsections: [
            { id: 'weightlifting', title: 'Weightlifting / Weight Training', desc: 'Traditional hypertrophy and strength building.', icon: '🏋️' },
            { id: 'calisthenics', title: 'Pull Up Bar / Calisthenics', desc: 'Upper body and bodyweight-focused strength.', icon: '💪' },
            { id: 'kettlebell', title: 'Kettlebell Training', desc: 'Functional power and conditioning.', icon: '🛎️' },
            { id: 'pilates', title: 'Pilates', desc: 'Core-focused training to improve posture and alignment.', icon: '🧘‍♂️' },
            { id: 'barre', title: 'Barre', desc: 'A blend of ballet, Pilates, and yoga for muscle endurance.', icon: '🩰' },
            { id: 'bands', title: 'Resistance Bands', desc: 'Low-impact muscle toning that is highly portable.', icon: '➰' },
            { id: 'trx', title: 'TRX (Suspension Training)', desc: 'Using gravity and straps for functional bodyweight strength.', icon: '⛓️' },
            { id: 'crossfit', title: 'CrossFit / Boot Camp', desc: 'Group-style functional fitness and circuit training.', icon: '🏋️‍♀️' }
        ]
    },
    'mindfulness': {
        title: 'Mindfulness & Mobility',
        icon: '🧠',
        image: '/images/fitness/mindfulness.png',
        desc: 'Find balance and recover faster.',
        subsections: [
            { id: 'yoga', title: 'Yoga', desc: 'Various styles such as Vinyasa, Hatha, and restorative.', icon: '🧘' },
            { id: 'stretching', title: 'Stretching', desc: 'Essential for flexibility and muscle recovery.', icon: '🧘‍♀️' },
            { id: 'meditation', title: 'Meditation & Breathwork', desc: 'Mental fitness sessions often included in holistic health apps.', icon: '🌬️' },
            { id: 'mobility', title: 'Mobility & Recovery', desc: 'Dynamic movement to improve joint health and prevent injury.', icon: '🤸' },
            { id: 'tai-chi', title: 'Tai Chi & Qigong', desc: 'Ancient low-impact practices for balance and mindfulness.', icon: '☯️' },
        ]
    },
    'specialized': {
        title: 'Specialized Activities',
        icon: '🌟',
        image: '/images/fitness/specialized activities.png',
        desc: 'Unique routines for specific environments.',
        subsections: [
            { id: 'swimming', title: 'Swimming & Aqua Fitness', desc: 'Joint-friendly resistance training in water.', icon: '🏊‍♀️' },
            { id: 'dance', title: 'Dance Fitness', desc: 'High-energy aerobic movement, such as Zumba.', icon: '💃' },
            { id: 'chair', title: 'Chair Fitness', desc: 'Specialized routines for older adults or limited mobility.', icon: '🪑' }
        ]
    }
};

export class Fitness {
    constructor(containerId) {
        this.containerId = containerId;
        this.viewState = {
            currentView: 'categories', // categories, subcategories, drills, player
            sectionId: null,
            subsectionId: null,
            activeDrill: null
        };
        this.history = [];
        this.gifCache = {};
        this.completedTodayIds = new Set();
        this.isPremium = false;
        this.unsubscribeWorkouts = null;
        this.unsubscribeUser = null;
        this.unsubscribeSubs = null;
        this.unsubscribeAuth = null;
        this.isLoadingSubscription = false;
        this.pendingAction = null;
        this.timer = null;
        this.timeLeft = 0;
        this.isPaused = false;
    }

    init() {
        const el = document.getElementById(this.containerId);
        if (!el) return;
        this.injectStyles();

        this.unsubscribeAuth = auth.onAuthStateChanged(user => {
            if (user) {
                this.setupUserSync(user.uid);
                if (this.pendingAction) {
                    const action = this.pendingAction;
                    this.pendingAction = null;
                    action();
                }
            } else {
                if (this.unsubscribeWorkouts) this.unsubscribeWorkouts();
                if (this.unsubscribeUser) this.unsubscribeUser();
                if (this.unsubscribeSubs) this.unsubscribeSubs();
                this.completedTodayIds.clear();
                this.isPremium = false;
                this.render();
            }
        });
    }

    destroy() {
        if (this.unsubscribeWorkouts) this.unsubscribeWorkouts();
        if (this.unsubscribeUser) this.unsubscribeUser();
        if (this.unsubscribeSubs) this.unsubscribeSubs();
        if (this.unsubscribeAuth) this.unsubscribeAuth();
        if (this.timer) clearInterval(this.timer);
        const styles = document.getElementById('fg-fitness-styles');
        if (styles) styles.remove();
    }

    setupUserSync(uid) {
        if (this.unsubscribeWorkouts) this.unsubscribeWorkouts();
        if (this.unsubscribeUser) this.unsubscribeUser();
        if (this.unsubscribeSubs) this.unsubscribeSubs();
        
        this.isLoadingSubscription = true;
        this.render();
        
        // Fetch full history for dashboard and real-time stats
        const historyRef = collection(db, 'users', uid, 'fitness_history');
        
        this.unsubscribeWorkouts = onSnapshot(historyRef, (snap) => {
            this.history = snap.docs.map(doc => doc.data());
            
            const startOfToday = new Date();
            startOfToday.setHours(0, 0, 0, 0);
            this.completedTodayIds.clear();

            this.history.forEach(item => {
                if (item.timestamp >= startOfToday.getTime()) {
                    this.completedTodayIds.add(item.drillId);
                }
            });
            this.render();
        }, (error) => {
            console.warn("[Fitness] Workouts subscription error:", error);
        });

        // Subscription & Role Check
        this.unsubscribeUser = onSnapshot(doc(db, 'users', uid), (snap) => {
            const data = snap.data();
            this.userRole = data?.role;
            this.userPremiumFlag = data?.premium || data?.isPremium;
            this._checkPremiumStatus();
        }, (error) => {
            console.warn("[Fitness] User doc subscription error:", error);
        });

        const subQuery = query(collection(db, 'users', uid, 'subscriptions'), where('status', 'in', ['active', 'trialing']));
        this.unsubscribeSubs = onSnapshot(subQuery, (snap) => {
            this.hasActiveSub = !snap.empty;
            this._checkPremiumStatus();
        }, (error) => {
            console.warn("[Fitness] Subscriptions subscription error:", error);
        });
    }

    _checkPremiumStatus() {
        // Check active subscription sub-collection OR the specific role field
        this.isPremium = this.hasActiveSub || 
                         this.userRole === 'premium' || 
                         this.userPremiumFlag === true;
        this.isLoadingSubscription = false;
        this.render();
    }

    _getRespectLevel(count) {
        if (count <= 10) return { num: 1, name: "Rookie" };
        if (count <= 30) return { num: 2, name: "Athlete" };
        return { num: 3, name: "Elite" };
    }

    getRespectBadge(level) {
        switch (level) {
            case 1: return '<span class="fg-badge-icon">⚪</span>';
            case 2: return '<span class="fg-badge-icon" style="filter: drop-shadow(0 0 5px var(--fitness-electric-blue));">🔵</span>';
            case 3: return '<span class="fg-badge-icon fg-badge-elite-glow">🔴</span>';
            default: return '⚪';
        }
    }

    render() {
        const el = document.getElementById(this.containerId);
        
        if (this.viewState.currentView === 'auth-modal') {
            el.innerHTML = this.renderAuthModal();
            this.attachAuthListeners(el);
            return;
        }

        if (this.viewState.currentView === 'player' || this.viewState.currentView === 'upgrade') {
            el.innerHTML = this.renderPlayerView();
            this.attachPlayerListeners(el);
            if (this.viewState.activeDrill) {
                this.fetchAndPlayGif(this.viewState.activeDrill.apiId);
            }
            return;
        }

        const breadcrumbs = this.renderBreadcrumbs();
        
        let content = '';
        if (this.isLoadingSubscription) {
            content = `<div style="padding: 100px; text-align: center;"><div class="fg-loader"></div><p style="margin-top: 20px; color: var(--text-secondary); font-weight: 600;">Verifying Clearances...</p></div>`;
        } else if (this.viewState.currentView === 'drills') {
            content = this.renderDrillListView();
        } else if (this.viewState.currentView === 'subcategories') {
            content = this.renderSectionView();
        } else if (this.viewState.currentView === 'dashboard') {
            content = this.renderDashboardView();
        } else {
            content = this.renderHomeView();
        }

        const main = `
            <div class="fg-fitness">
                ${breadcrumbs}
                ${content}
            </div>
        `;

        el.innerHTML = sidebarPage({ 
            title: 'football<span class="fg-brand-girl">girl</span>Fit', 
            subtitle: 'Train your way, anywhere, anytime.',
            main, 
            wide: true,
            pageClass: 'fg-fitness-page'
        });

        this.attachListeners(el);
    }

    renderBreadcrumbs() {
        let html = `<nav class="fg-fit-breadcrumbs">
            <span class="crumb" data-view="home">football<span class="fg-brand-girl">girl</span>Fit</span>`;
        
        if (this.viewState.sectionId) {
            const section = FITNESS_DATA[this.viewState.sectionId];
            html += ` <span class="sep">/</span> <span class="crumb" data-view="section" data-id="${this.viewState.sectionId}">${section.title}</span>`;
        }
        
        if (this.viewState.currentView === 'dashboard') {
            html += ` <span class="sep">/</span> <span class="crumb active">Dashboard</span>`;
        } else if (this.viewState.subsectionId) {
            const section = FITNESS_DATA[this.viewState.sectionId];
            const sub = section.subsections.find(s => s.id === this.viewState.subsectionId);
            html += ` <span class="sep">/</span> <span class="crumb active">${sub.title}</span>`;
        }
        
        html += `</nav>`;
        return html;
    }

    authCheck(action) {
        if (auth.currentUser) {
            action();
        } else {
            this.pendingAction = action;
            this.viewState.currentView = 'auth-modal';
            this.render();
        }
    }

    renderAuthModal() {
        return `
            <div class="fg-auth-overlay">
                <div class="fg-auth-modal-glass fadeInSlideUp">
                    <!-- Main Auth View -->
                    <div class="fg-auth-center" id="auth-main-view">
                        <div class="fg-auth-logo-pulse">⚽</div>
                        <h2>Join the Elite</h2>
                        <p>Sign in to track your Respect Level and unlock professional drills.</p>
                        
                        <button class="fg-google-auth-btn" id="google-sign-in">
                            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/layout/google.svg" alt="Google">
                            Sign in with Google
                        </button>

                        <div class="fg-auth-divider"><span>OR</span></div>

                        <input type="email" id="auth-email" class="fg-auth-input" placeholder="Email Address">
                        <input type="password" id="auth-password" class="fg-auth-input" placeholder="Password">
                        
                        <div style="text-align: right; width: 100%; margin-top: -4px; margin-bottom: 12px;">
                            <button class="fg-auth-link-btn" id="fg-forgot-password-trigger">Forgot password?</button>
                        </div>

                        <button class="fg-auth-action-btn" id="email-sign-in">Sign In / Join Now</button>
                        
                        <button class="fg-auth-close-btn" data-view="home">Maybe Later</button>
                    </div>

                    <!-- Reset Password View -->
                    <div class="fg-auth-center" id="auth-reset-view" style="display: none;">
                        <div class="fg-auth-logo-pulse">📧</div>
                        <h2>Reset Password</h2>
                        <p>Enter your email and we'll send you a recovery link.</p>
                        
                        <input type="email" id="reset-email" class="fg-auth-input" placeholder="Email Address">
                        
                        <div id="reset-status-msg" style="display: none; font-size: 0.85rem; margin-bottom: 12px; padding: 10px; border-radius: 8px;"></div>

                        <button class="fg-auth-action-btn" id="fg-btn-send-reset">Send Reset Link</button>
                        
                        <button class="fg-auth-link-btn" id="fg-back-to-login" style="margin-top: 20px;">← Back to Sign In</button>
                    </div>
                </div>
            </div>
        `;
    }

    renderDashboardView() {
        const totalDrills = this.history.length;
        const level = this._getRespectLevel(totalDrills);
        const badge = this.getRespectBadge(level.num);
        const userName = auth.currentUser?.displayName || 'Athlete';
        
        let progress = 0;
        if (level.num === 1) progress = (totalDrills / 10) * 100;
        else if (level.num === 2) progress = ((totalDrills - 10) / 20) * 100;
        else progress = 100;

        // 2. Interactive Heatmap (Sunday to Saturday)
        const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const now = new Date();
        const currentDayIdx = now.getDay(); 
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - currentDayIdx);
        startOfWeek.setHours(0, 0, 0, 0);

        const weekActivity = weekDays.map((label, index) => {
            const d = new Date(startOfWeek);
            d.setDate(startOfWeek.getDate() + index);
            const dStr = d.toISOString().split('T')[0];
            
            const active = this.history.some(item => {
                return new Date(item.timestamp).toISOString().split('T')[0] === dStr;
            });
            
            return { label, active };
        });

        // 3. Favorite Zone & Streak Logic
        const counts = {};
        this.history.forEach(item => {
            const zone = this.getDrillCategory(item.drillId);
            counts[zone] = (counts[zone] || 0) + 1;
        });

        let favoriteZone = 'None';
        let maxCount = 0;
        for (const [zone, count] of Object.entries(counts)) {
            if (count > maxCount) { maxCount = count; favoriteZone = zone; }
        }

        const streak = this.calculateStreak();

        return `
            <button class="fg-fit-back-btn" data-view="home">← Back to Training</button>
            <div class="fg-dashboard fadeInSlideUp">
                <section class="fg-dashboard__section">
                    <div class="fg-respect-header">
                        <h3 style="display: flex; align-items: center; gap: 8px;">${badge} ${userName} — Level ${level.num}</h3>
                        <span>${totalDrills} / ${level.num === 3 ? totalDrills : (level.num === 1 ? 10 : 30)} Drills</span>
                    </div>
                    <div class="fg-respect-meter">
                        <div class="fg-respect-meter__fill" style="width: ${progress}%" title="${Math.round(progress)}% to next level"></div>
                    </div>
                </section>

                <div class="fg-dashboard__stats-grid">
                    <div class="fg-stat-card">
                        <label>TOTAL DRILLS</label>
                        <strong>${totalDrills}</strong>
                    </div>
                    <div class="fg-stat-card glass">
                        <label>CURRENT STREAK</label>
                        <strong style="color: var(--fitness-vibrant-pink); display: flex; align-items: center; gap: 8px;">
                            ${streak} <span style="font-size: 1.2rem;">🔥</span>
                        </strong>
                    </div>
                    <div class="fg-stat-card glass" style="grid-column: span 2;">
                        <label>FAVORITE ZONE</label>
                        <div class="fg-pill-badge">${favoriteZone}</div>
                    </div>
                </div>

                <section class="fg-dashboard__section">
                    <h3 style="font-size: 0.75rem; font-weight: 800; color: var(--text-secondary); text-transform: uppercase; margin-bottom: 16px;">Weekly Activity</h3>
                    <div class="fg-heatmap">
                        ${weekActivity.map(d => `
                            <div class="fg-heatmap__day ${d.active ? 'is-active' : ''}">
                                <span class="day-label">${d.label}</span>
                            </div>
                        `).join('')}
                    </div>
                </section>
            </div>
        `;
    }

    calculateStreak() {
        if (this.history.length === 0) return 0;
        
        // Extract unique YYYY-MM-DD strings
        const dates = [...new Set(this.history.map(item => {
            return new Date(item.timestamp).toISOString().split('T')[0];
        }))].sort().reverse();

        const today = new Date().toISOString().split('T')[0];
        const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];

        // Start checking from today, or yesterday if today isn't logged yet
        let streak = 0;
        let currentDateStr = dates[0] === today ? today : (dates[0] === yesterday ? yesterday : null);

        if (!currentDateStr) return 0;

        let checkDate = new Date(currentDateStr);
        while (dates.includes(checkDate.toISOString().split('T')[0])) {
            streak++;
            // Move back one day
            checkDate.setDate(checkDate.getDate() - 1);
        }

        return streak;
    }

    renderHomeView() {
        const level = this._getRespectLevel(this.history.length);
        const badge = this.getRespectBadge(level.num);

        return `
            <div class="fg-fit-header-actions" style="margin-bottom: 32px; display: flex; justify-content: flex-end; align-items: center;">
                <div style="display: flex; align-items: center; gap: 16px;">
                    <div class="fg-header-respect-pill">${badge} <span>${this.history.length}</span></div>
                    <button class="fg-btn-dashboard-toggle" style="background: var(--fitness-electric-blue); color: #fff; border: none; padding: 12px 24px; border-radius: 99px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 8px;">
                        📊 View Stats Dashboard
                    </button>
                </div>
            </div>
            <div class="fg-fit-grid fg-fit-grid--main">
                ${Object.entries(FITNESS_DATA).map(([id, data]) => `
                    <div class="fg-fit-card fg-fit-card--large" data-section="${id}" 
                         style="background-image: linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url('${data.image}'); 
                                background-size: cover; background-position: center; border: none; color: white;">
                        <div class="fg-fit-card__icon" style="color: #fff; text-shadow: 0 2px 10px rgba(0,0,0,0.3);">${data.icon}</div>
                        <h2 class="fg-fit-card__title" style="color: #fff; text-shadow: 0 2px 10px rgba(0,0,0,0.3);">${data.title}</h2>
                    </div>
                `).join('')}
            </div>
        `;
    }

    renderSectionView() {
        const section = FITNESS_DATA[this.viewState.sectionId];
        return `
            <button class="fg-fit-back-btn" data-view="home">← Back to Categories</button>
            <div class="fg-fit-grid fg-fit-grid--sub">
                ${section.subsections.map(sub => {
                    const dataKey = SUBSECTION_MAPPING[sub.id];
                    const isLocked = fitnessData[dataKey]?.isPremium && !this.isPremium;

                    return `
                        <div class="fg-fit-card fg-fit-card--sub ${isLocked ? 'fg-fit-card--locked' : ''}" 
                             data-section="${this.viewState.sectionId}" 
                             data-subsection="${sub.id}"
                             data-locked="${isLocked}">
                            ${isLocked ? '<div class="fg-lock-corner">🔒</div><div class="fg-premium-badge">PREMIUM</div>' : ''}
                            <div class="fg-fit-card__icon">${sub.icon}</div>
                            <div class="fg-fit-card__body">
                                <h3 class="fg-fit-card__title">${sub.title}</h3>
                                <p class="fg-fit-card__desc">${sub.desc}</p>
                            </div>
                        </div>
                    `;
                }).join('')}
            </div>
        `;
    }

    renderDrillListView() {
        const section = FITNESS_DATA[this.viewState.sectionId];
        const sub = section.subsections.find(s => s.id === this.viewState.subsectionId);
        
        const dataKey = SUBSECTION_MAPPING[sub.id];
        const drills = fitnessData[dataKey]?.drills || [];

        return `
            <button class="fg-fit-back-btn" data-view="subcategories" data-id="${this.viewState.sectionId}">← Back to ${section.title}</button>
            <div class="fg-fit-content">
                <div class="fg-fit-content__header">
                    <div class="fg-fit-content__icon">${sub.icon}</div> 
                    <h1>${sub.title}</h1>
                    <p>${sub.desc}</p>
                </div>
                <div class="fg-drill-list">
                    ${drills.map(drill => `
                        <div class="fg-drill-row">
                            <div class="fg-drill-row__main">
                                <h3 class="fg-drill-row__title">${drill.name}</h3>
                                <div class="fg-drill-row__meta">
                                    <span class="fg-drill-tag">${drill.duration}</span>
                                    <span class="fg-drill-tag intensity-${drill.intensity.toLowerCase()}">${drill.intensity}</span>
                                </div>
                            </div>
                            ${this.completedTodayIds.has(drill.id) 
                                ? '<div class="fg-drill-checkmark">✓ Completed</div>' 
                                : `<button class="fg-btn-start" data-drill-id="${drill.id}">Start</button>`
                            }
                        </div>
                    `).join('')}
                </div>
            </div>
        `;
    }

    renderPlayerView() {
        if (this.viewState.currentView === 'upgrade') {
            return `
                <div class="fg-fit-player" style="background: rgba(0,0,0,0.6); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); justify-content: center; align-items: center;">
                    <div class="fg-modal fg-upgrade-modal glass" style="display:block; background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); color: #fff; padding: 48px; text-align: center;">
                        <h2 style="font-weight: 900; letter-spacing: -1px; color: #fff; font-size: 2rem; margin-bottom: 16px;">Unlock the Full Super-App</h2>
                        <p style="color: rgba(255,255,255,0.7); font-size: 1.1rem; line-height: 1.6; margin-bottom: 32px;">Get exclusive access to <strong>all 220+ high-tech drills</strong>, advanced performance analytics, and personalized professional training plans.</p>
                        <button class="fg-btn--unlock" id="fg-premium-unlock">Unlock Now — €9.99/mo</button>
                        <button class="fg-btn" id="player-exit" style="margin-top: 24px; border:none; background:none; color:rgba(255,255,255,0.4); font-weight: 600;">Maybe Later</button>
                    </div>
                </div>
            `;
        }

        const drill = this.viewState.activeDrill;
        const isTimerBased = drill.duration.includes('s');
        
        return `
            <div class="fg-fit-player">
                <div class="fg-fit-player__media">
                    <div class="fg-player-gif-loading">Loading High-Tech Feed...</div>
                    <img src="" id="fg-player-gif" alt="${drill.name}" style="display:none;">
                    
                    <div class="fg-player-tip-bubble glass">
                        <span class="tip-icon">💡</span>
                        <p>${drill.tip}</p>
                    </div>
                </div>

                <div class="fg-player-overlay">
                    <div class="fg-player-stats glass">
                        <div class="stat-item">
                            <label>EXERCISE</label>
                            <strong>${drill.name}</strong>
                        </div>
                        <div class="stat-item">
                            <label>INTENSITY</label>
                            <strong style="color: #007AFF;">${drill.intensity}</strong>
                        </div>
                    </div>

                    <div class="fg-player-timer-area">
                        ${isTimerBased ? `
                            <div class="fg-countdown-timer" id="fg-timer-display">00:00</div>
                        ` : `
                            <div class="fg-rep-display">${drill.duration}</div>
                            <button class="fg-btn-done" id="fg-drill-done">DONE</button>
                        `}
                    </div>

                    <div class="fg-player-controls glass">
                        <button class="player-ctrl-btn" id="player-exit">EXIT</button>
                        ${isTimerBased ? `<button class="player-ctrl-btn primary" id="player-pause">PAUSE</button>` : ''}
                    </div>
                </div>
            </div>
        `;
    }

    async handleComplete(drillId) {
        const user = auth.currentUser;
        if (!user) return;

        const drill = this.viewState.activeDrill || { name: 'Unknown Exercise' };

        try {
            await addDoc(collection(db, 'users', user.uid, 'fitness_history'), {
                drillId: drillId,
                drillName: drill.name,
                timestamp: Date.now(),
                status: 'completed'
            });
            this._showToast("Workout Saved", "vibrant-pink");
        } catch (e) {
            console.error("Error saving workout:", e);
            this._showToast("Failed to save workout. Please try again.", "electric-blue");
        }
    }

    _showToast(msg, theme = 'vibrant-pink', duration = 3000) {
        const toast = document.createElement('div');
        toast.className = 'fg-toast';
        toast.style.background = theme === 'vibrant-pink' ? '#FF2D55' : '#007AFF';
        toast.textContent = msg;
        document.body.appendChild(toast);
        requestAnimationFrame(() => toast.classList.add('fg-toast--visible'));
        setTimeout(() => {
            toast.classList.remove('fg-toast--visible');
            setTimeout(() => toast.remove(), 300);
        }, duration);
    }

    async fetchAndPlayGif(apiId) {
        const apiKey = import.meta.env.VITE_RAPIDAPI_KEY;
        const img = document.getElementById('fg-player-gif');
        const loader = document.querySelector('.fg-player-gif-loading');
        
        try {
            let gifUrl = this.gifCache[apiId];
            if (!gifUrl) {
                const resp = await fetch(`https://exercisedb.p.rapidapi.com/exercises/exercise/${apiId}`, {
                    headers: { 'x-rapidapi-key': apiKey, 'x-rapidapi-host': 'exercisedb.p.rapidapi.com' }
                });
                const data = await resp.json();
                gifUrl = data.gifUrl;
                this.gifCache[apiId] = gifUrl;
            }
            if (gifUrl && img) {
                img.src = gifUrl;
                img.onload = () => { 
                    img.style.display = 'block'; 
                    if (loader) loader.style.display = 'none'; 
                };
            }
        } catch (e) { console.error("Player GIF Load Error:", e); }
    }

    getDrillCategory(drillId) {
        for (const [catId, catData] of Object.entries(FITNESS_DATA)) {
            for (const sub of catData.subsections) {
                const dataKey = SUBSECTION_MAPPING[sub.id];
                const drills = fitnessData[dataKey]?.drills || [];
                if (drills.some(d => d.id === drillId)) return catData.title;
            }
        }
        return 'General';
    }

    injectStyles() {
        const styleId = 'fg-fitness-styles';
        if (document.getElementById(styleId)) return;
        const css = `
            @keyframes fadeInSlideUp {
                from { opacity: 0; transform: translateY(20px); }
                to { opacity: 1; transform: translateY(0); }
            }

            .fg-fit-content, .fg-fit-grid, .fg-drill-list, .fg-fit-player, .fg-dashboard {
                animation: fadeInSlideUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
            }

            .fg-fitness-page { 
                background: linear-gradient(135deg, #FFFFFF 0%, #f0f2f5 100%);
                min-height: 100vh;
            }
            .fg-fitness { max-width: 1200px; margin: 0 auto; text-align: left; }

            .fg-loader { width: 40px; height: 40px; border: 4px solid var(--border-color); border-top-color: var(--primary-blue); border-radius: 50%; animation: fg-spin 1s linear infinite; margin: 0 auto; }
            @keyframes fg-spin { to { transform: rotate(360deg); } }

            .fg-fit-card--locked { filter: grayscale(80%); opacity: 0.8; position: relative; }
            .fg-lock-corner { position: absolute; top: 18px; right: 18px; font-size: 1.4rem; color: #FFD700; filter: drop-shadow(0 0 8px rgba(255, 215, 0, 0.4)); z-index: 10; }
            .fg-premium-badge { position: absolute; top: 12px; right: 12px; background: #007AFF; color: #fff; font-size: 0.6rem; font-weight: 900; padding: 4px 10px; border-radius: 6px; letter-spacing: 0.5px; z-index: 5; }
            .fg-completed-badge { display: inline-flex; align-items: center; gap: 4px; color: #22c55e; font-size: 0.75rem; font-weight: 700; margin-top: 8px; }

            .fg-upgrade-modal { max-width: 400px; text-align: center; padding: 40px; border-radius: 24px; background: #fff; box-shadow: 0 20px 50px rgba(0,0,0,0.3); }
            .fg-upgrade-modal h2 { color: #000; margin-bottom: 12px; }
            .fg-btn--unlock { background: #FF2D55; color: #fff; border: none; padding: 16px; border-radius: 99px; font-weight: 800; font-size: 1rem; width: 100%; cursor: pointer; box-shadow: 0 10px 20px rgba(255,45,85,0.2); transition: transform 0.2s; }
            .fg-btn--unlock:hover { transform: scale(1.02); }
            
            .fg-fit-breadcrumbs { margin-bottom: 24px; font-size: 0.9rem; color: #8a8d91; font-weight: 600; display: flex; align-items: center; }
            .fg-fit-breadcrumbs .crumb { cursor: pointer; transition: color 0.2s; }
            .fg-fit-breadcrumbs .crumb:hover:not(.active) { color: #007AFF; }
            .fg-fit-breadcrumbs .crumb.active { cursor: default; color: #FF2D55; }
            .fg-fit-breadcrumbs .sep { margin: 0 12px; color: #ccc; }

            .glass { background: rgba(255, 255, 255, 0.7); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); border: 1px solid rgba(255, 255, 255, 0.4); }

            .fg-fit-back-btn { 
                background: rgba(255, 255, 255, 0.8); backdrop-filter: blur(12px); border: 1px solid rgba(0,0,0,0.05); 
                padding: 10px 24px; border-radius: 99px; font-weight: 700; font-size: 0.85rem; cursor: pointer;
                margin-bottom: 32px; color: #007AFF; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                box-shadow: 0 4px 12px rgba(0,0,0,0.03);
            }

            .fg-fit-grid { display: grid; gap: 24px; }
            .fg-fit-grid--main { grid-template-columns: repeat(2, 1fr); }
            
            .fg-fit-card { 
                background: rgba(255, 255, 255, 0.7); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px);
                border-radius: 24px; border: 1px solid rgba(255, 255, 255, 0.4);
                padding: 60px 40px; text-align: center; cursor: pointer; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                display: flex; flex-direction: column; align-items: center; justify-content: center;
                box-shadow: 0 10px 30px rgba(0,0,0,0.04);
            }
            .fg-fit-card:hover { transform: translateY(-8px) scale(1.01); box-shadow: 0 20px 40px rgba(0,0,0,0.08); border-color: #007AFF; }
            
            .fg-fit-card__icon { font-size: 4.5rem; margin-bottom: 24px; transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); color: #007AFF; }
            .fg-fit-card:hover .fg-fit-card__icon { transform: scale(1.2) rotate(5deg); color: var(--fitness-vibrant-pink); }
            .fg-fit-card__title { font-size: 1.65rem; font-weight: 800; color: #000; margin: 0; }
            
            .fg-fit-grid--sub { grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); }
            .fg-fit-card--sub { padding: 32px; text-align: left; align-items: flex-start; flex-direction: row; gap: 24px; }
            .fg-fit-card--sub .fg-fit-card__icon { font-size: 2.75rem; margin-bottom: 0; }
            .fg-fit-card--sub .fg-fit-card__title { font-size: 1.25rem; margin-bottom: 6px; }
            .fg-fit-card__desc { font-size: 0.95rem; color: var(--text-secondary); margin: 0; line-height: 1.6; }

            .fg-fit-content { text-align: center; padding: 60px 0; }
            .fg-fit-content__header { margin-bottom: 48px; }
            .fg-fit-content__icon { font-size: 6rem; margin-bottom: 20px; }
            .fg-fit-content h1 { font-size: 2.75rem; font-weight: 800; margin-bottom: 16px; color: #007AFF; }
            .fg-fit-content p { color: var(--text-secondary); max-width: 650px; margin: 0 auto; font-size: 1.15rem; line-height: 1.6; }
            
            .fg-drill-list { display: flex; flex-direction: column; gap: 12px; margin-top: 32px; }
            .fg-drill-row { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; background: #fff; border: 1px solid var(--border-color); border-radius: 16px; transition: all 0.2s; }
            .fg-drill-row:hover { border-color: #007AFF; box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
            .fg-drill-row__title { font-size: 1.1rem; font-weight: 700; margin: 0 0 4px; }
            .fg-drill-row__meta { display: flex; gap: 8px; }
            .fg-drill-tag { font-size: 0.75rem; font-weight: 700; padding: 2px 8px; border-radius: 4px; background: #f0f2f5; color: #65676b; }
            .fg-drill-tag.intensity-extreme { background: #fff1f2; color: #FF2D55; }
            .fg-drill-checkmark { color: #007AFF; font-weight: 800; font-size: 0.9rem; }
            .fg-btn-start { background: #FF2D55; color: #fff; border: none; padding: 10px 24px; border-radius: 99px; font-weight: 700; cursor: pointer; transition: transform 0.2s; }
            .fg-btn-start:hover { transform: scale(1.05); background: var(--fitness-vibrant-pink); }

            .fg-fit-player { position: fixed; inset: 0; background: #000; z-index: 10000; display: flex; flex-direction: column; }
            .fg-fit-player__media { flex: 1; position: relative; display: flex; align-items: center; justify-content: center; }
            .fg-player-gif-loading { color: #fff; opacity: 0.5; font-weight: 700; }
            #fg-player-gif { max-height: 80vh; max-width: 90vw; object-fit: contain; }
            
            .fg-player-tip-bubble { position: absolute; bottom: 120px; left: 50%; transform: translateX(-50%); padding: 16px 24px; border-radius: 20px; max-width: 400px; display: flex; gap: 12px; color: #000; }
            .tip-icon { font-size: 1.5rem; }
            .fg-player-tip-bubble p { margin: 0; font-size: 0.9rem; font-weight: 500; line-height: 1.4; }

            .fg-player-overlay { position: absolute; inset: 0; pointer-events: none; display: flex; flex-direction: column; justify-content: space-between; padding: 40px; }
            .fg-player-stats { pointer-events: auto; padding: 20px 32px; border-radius: 20px; display: flex; gap: 40px; align-self: flex-start; }
            .stat-item label { display: block; font-size: 0.65rem; font-weight: 800; color: var(--text-secondary); margin-bottom: 4px; }
            .stat-item strong { font-size: 1.25rem; font-weight: 800; }

            .fg-player-timer-area { align-self: center; display: flex; flex-direction: column; align-items: center; gap: 20px; }
            .fg-countdown-timer { font-size: 8rem; font-weight: 900; color: #fff; font-variant-numeric: tabular-nums; text-shadow: 0 0 40px rgba(0,122,255,0.5); }
            .fg-rep-display { font-size: 5rem; font-weight: 900; color: #fff; }
            .fg-btn-done { pointer-events: auto; background: #FF2D55; color: #fff; border: none; padding: 20px 60px; border-radius: 99px; font-size: 1.5rem; font-weight: 800; cursor: pointer; box-shadow: 0 10px 30px rgba(255,45,85,0.3); }

            .fg-player-controls { pointer-events: auto; align-self: center; padding: 12px; border-radius: 99px; display: flex; gap: 8px; }
            .player-ctrl-btn { background: rgba(0,0,0,0.5); border: none; color: #fff; padding: 12px 32px; border-radius: 99px; font-weight: 700; cursor: pointer; transition: background 0.2s; }
            .player-ctrl-btn.primary { background: #FF2D55; }

            .fg-badge-elite-glow { 
                display: inline-block;
                filter: drop-shadow(0 0 8px #FF2D55);
                animation: fg-badge-pulse 2s infinite ease-in-out;
            }
            @keyframes fg-badge-pulse {
                0%, 100% { transform: scale(1); opacity: 1; }
                50% { transform: scale(1.1); opacity: 0.8; }
            }

            .fg-header-respect-pill { display: flex; align-items: center; gap: 6px; background: #fff; border: 1px solid var(--border-color); padding: 6px 12px; border-radius: 99px; font-weight: 800; font-size: 0.85rem; }

            /* Dashboard Styles */
            .fg-dashboard__section { margin-bottom: 40px; }
            .fg-respect-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 12px; }
            .fg-respect-header h3 { margin: 0; font-weight: 800; font-size: 1.2rem; }
            .fg-respect-header span { color: var(--text-secondary); font-size: 0.9rem; font-weight: 600; }
            .fg-respect-meter { height: 20px; background: #e4e6eb; border-radius: 10px; overflow: hidden; }
            .fg-respect-meter__fill { height: 100%; background: linear-gradient(90deg, var(--fitness-vibrant-pink) 0%, var(--fitness-electric-blue) 100%); transition: width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1); }
            
            .fg-dashboard__stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 20px; margin-bottom: 40px; }
            .fg-stat-card { background: #fff; padding: 24px; border-radius: 20px; border: 1px solid var(--border-color); box-shadow: 0 4px 12px rgba(0,0,0,0.02); }
            .fg-stat-card label { display: block; font-size: 0.7rem; font-weight: 800; color: var(--text-secondary); margin-bottom: 8px; letter-spacing: 0.5px; }
            .fg-stat-card strong { font-size: 1.5rem; font-weight: 900; color: #000; }

            .fg-heatmap { display: grid; grid-template-columns: repeat(7, 1fr); gap: 12px; }
            .fg-heatmap__day { 
                background: #fff; padding: 16px 8px; border-radius: 16px; border: 2px solid transparent; 
                display: flex; flex-direction: column; align-items: center; gap: 10px;
                transition: all 0.3s ease;
            }
            .fg-heatmap__day.is-active { 
                border-color: var(--fitness-vibrant-pink); 
                box-shadow: 0 0 15px rgba(255, 45, 85, 0.2); 
            }
            .day-label { font-size: 0.75rem; font-weight: 700; color: var(--text-secondary); }
            .day-dot { width: 10px; height: 10px; border-radius: 50%; background: #eee; }
            .fg-heatmap__day.is-active .day-dot { background: var(--fitness-vibrant-pink); transform: scale(1.2); }
            
            .fg-btn-dashboard-toggle:hover { transform: translateY(-2px); filter: brightness(1.1); }
            
            @media (max-width: 768px) {
                .fg-fit-grid--main { grid-template-columns: 1fr; }
                .fg-fit-card { padding: 40px 24px; }
                .fg-fit-card__icon { font-size: 3.5rem; }
                .fg-fit-card--sub { flex-direction: column; align-items: center; text-align: center; padding: 32px 24px; }
                .fg-fit-card--sub .fg-fit-card__icon { margin-bottom: 12px; }
                .fg-fit-content h1 { font-size: 2rem; }
                .fg-fit-content p { font-size: 1rem; }
                .fg-countdown-timer { font-size: 5rem; }
                .fg-player-stats { width: 100%; gap: 20px; padding: 16px; }
            }
        `;
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = css;
        document.head.appendChild(style);
    }

    attachListeners(el) {
        el.onclick = (e) => {
            const sectionCard = e.target.closest('.fg-fit-card--large');
            const subCard = e.target.closest('.fg-fit-card--sub');
            const startBtn = e.target.closest('.fg-btn-start');
            const crumb = e.target.closest('.crumb');
            const backBtn = e.target.closest('.fg-fit-back-btn');
            const dashboardToggle = e.target.closest('.fg-btn-dashboard-toggle');
            const authClose = e.target.closest('.fg-auth-close-btn');

            if (authClose) {
                this.viewState.currentView = authClose.dataset.view;
                this.pendingAction = null;
                this.render();
                return;
            }

            if (sectionCard) {
                this.authCheck(() => {
                    this.viewState.currentView = 'subcategories';
                    this.viewState.sectionId = sectionCard.dataset.section;
                    this.render();
                    window.scrollTo(0, 0);
                });
            } else if (subCard) {
                if (subCard.dataset.locked === 'true' && !this.isPremium) { // Only show upgrade if user is NOT premium
                    this.viewState.currentView = 'upgrade';
                    this.render();
                    return;
                }
                this.viewState.currentView = 'drills';
                this.viewState.subsectionId = subCard.dataset.subsection;
                this.render();
                window.scrollTo(0, 0);
            } else if (startBtn) {
                const sub = FITNESS_DATA[this.viewState.sectionId].subsections.find(s => s.id === this.viewState.subsectionId);
                const dataKey = SUBSECTION_MAPPING[sub.id];
                const drill = fitnessData[dataKey].drills.find(d => d.id === startBtn.dataset.drillId);
                
                this.viewState.currentView = 'player';
                this.viewState.activeDrill = drill;
                this.render();
            } else if (dashboardToggle) {
                this.authCheck(() => {
                    this.viewState.currentView = 'dashboard';
                    this.render();
                    window.scrollTo(0, 0);
                });
            } else if (crumb || backBtn) {
                const view = (crumb || backBtn).dataset.view;
                this.viewState.currentView = view;
                if (view === 'home') {
                    this.viewState.sectionId = null;
                    this.viewState.subsectionId = null;
                } else if (view === 'subcategories') {
                    this.viewState.subsectionId = null;
                }
                this.render();
                window.scrollTo(0, 0);
            }
        };
    }

    attachAuthListeners(el) {
        const mainView = el.querySelector('#auth-main-view');
        const resetView = el.querySelector('#auth-reset-view');
        const resetMsg = el.querySelector('#reset-status-msg');

        el.querySelector('#google-sign-in')?.addEventListener('click', async () => {
            try {
                await signInWithGoogle();
            } catch (err) {
                console.error("Auth Error:", err);
                alert("Authentication failed. Please try again.");
            }
        });

        // Toggle to Reset View
        el.querySelector('#fg-forgot-password-trigger')?.addEventListener('click', () => {
            mainView.style.display = 'none';
            resetView.style.display = 'block';
        });

        // Toggle back to Main View
        el.querySelector('#fg-back-to-login')?.addEventListener('click', () => {
            resetView.style.display = 'none';
            mainView.style.display = 'block';
            resetMsg.style.display = 'none';
        });

        // Handle Reset Password Submission
        el.querySelector('#fg-btn-send-reset')?.addEventListener('click', async () => {
            const email = el.querySelector('#reset-email').value;
            if (!email) return alert("Please enter your email address.");

            const btn = el.querySelector('#fg-btn-send-reset');
            btn.disabled = true;
            btn.textContent = 'Sending...';

            try {
                await resetPassword(email);
                resetMsg.textContent = "Success! Check your inbox for the reset link.";
                resetMsg.style.display = 'block';
                resetMsg.style.background = 'rgba(34, 197, 94, 0.1)';
                resetMsg.style.color = '#22c55e';
            } catch (err) {
                resetMsg.textContent = err.message;
                resetMsg.style.display = 'block';
                resetMsg.style.background = 'rgba(255, 45, 85, 0.1)';
                resetMsg.style.color = '#FF2D55';
                btn.disabled = false;
                btn.textContent = 'Send Reset Link';
            }
        });
    }

    attachPlayerListeners(el) {
        const drill = this.viewState.activeDrill;
        const isTimerBased = drill.duration.includes('s');

        el.querySelector('#fg-premium-unlock')?.addEventListener('click', async () => {
            const user = auth.currentUser;
            if (!user) return;

            const btn = el.querySelector('#fg-premium-unlock');
            btn.disabled = true;
            btn.textContent = 'Preparing Secure Checkout...';

            try {
                const sessionsRef = collection(db, 'users', user.uid, 'checkout_sessions');
                const docRef = await addDoc(sessionsRef, {
                    price: 'price_1TKTsO7bfPu8CZKM9D9ywBX8',
                    success_url: window.location.origin,
                    cancel_url: window.location.origin,
                });

                onSnapshot(docRef, (snap) => {
                    const url = snap.data()?.url;
                    if (url) window.location.assign(url);
                });
            } catch (err) {
                console.error("Stripe Checkout Error:", err);
                btn.disabled = false;
                btn.textContent = 'Unlock Now — €9.99/mo';
                alert("Checkout unavailable. Please check your connection and try again.");
            }
        });

        if (this.viewState.currentView === 'upgrade') return;

        if (isTimerBased) {
            const seconds = parseInt(drill.duration);
            this.startTimer(seconds);
        }

        el.querySelector('#player-exit')?.addEventListener('click', () => this.exitPlayer());
        
        el.querySelector('#player-pause')?.addEventListener('click', (e) => {
            this.isPaused = !this.isPaused;
            e.target.textContent = this.isPaused ? 'RESUME' : 'PAUSE';
        });

        el.querySelector('#fg-drill-done')?.addEventListener('click', async () => {
            await this.handleComplete(drill.id);
            this.exitPlayer();
        });
    }

    startTimer(seconds) {
        if (this.timer) clearInterval(this.timer);
        this.timeLeft = seconds;
        this.isPaused = false;
        this.updateTimerDisplay();
        
        this.timer = setInterval(() => {
            if (!this.isPaused) {
                this.timeLeft--;
                this.updateTimerDisplay();
                if (this.timeLeft <= 0) {
                    clearInterval(this.timer);
                    this.handleComplete(this.viewState.activeDrill.id);
                    this.exitPlayer();
                }
            }
        }, 1000);
    }

    updateTimerDisplay() {
        const display = document.getElementById('fg-timer-display');
        if (!display) return;
        const m = Math.floor(this.timeLeft / 60);
        const s = this.timeLeft % 60;
        display.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }

    exitPlayer() {
        if (this.timer) clearInterval(this.timer);
        this.viewState.currentView = 'drills';
        this.viewState.activeDrill = null;
        this.render();
    }
}